import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getRandomTruthOrDare } from '../store/gameStore';

interface TruthOrDareProps {
  onComplete: (success: boolean) => void;
}

const TruthOrDare: React.FC<TruthOrDareProps> = ({ onComplete }) => {
  const [phase, setPhase] = useState<'sip' | 'choose' | 'prompt' | 'done'>('sip');
  const [currentPrompt, setCurrentPrompt] = useState<{ type: 'truth' | 'dare'; prompt: string } | null>(null);

  const handleSip = () => {
    setPhase('choose');
  };

  const handleChoose = (type: 'truth' | 'dare') => {
    const prompt = getRandomTruthOrDare(type);
    setCurrentPrompt(prompt);
    setPhase('prompt');
  };

  const handleComplete = (success: boolean) => {
    setPhase('done');
    setTimeout(() => onComplete(success), 500);
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-br from-orange-900 to-red-900 rounded-3xl p-8 max-w-lg w-full shadow-2xl border border-orange-500/30"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
      >
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white mb-2">🎭 TRUTH OR DARE & SIP</h2>
          <p className="text-orange-300">Complete to roll dice and move forward!</p>
        </div>

        <AnimatePresence mode="wait">
          {phase === 'sip' && (
            <motion.div
              key="sip"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="bg-black/30 rounded-2xl p-6 mb-6">
                <motion.div
                  className="text-6xl mb-4"
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  🍺
                </motion.div>
                <p className="text-xl text-white font-bold mb-2">TAKE A SIP FIRST!</p>
                <p className="text-orange-300 text-sm">You must take a sip of your drink before choosing Truth or Dare.</p>
              </div>

              <motion.button
                onClick={handleSip}
                className="py-4 px-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-black font-bold text-xl shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🍺 I Sipped!
              </motion.button>
            </motion.div>
          )}

          {phase === 'choose' && (
            <motion.div
              key="choose"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-2 gap-4"
            >
              <motion.button
                onClick={() => handleChoose('truth')}
                className="py-8 px-6 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl text-white font-bold text-xl shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-4xl block mb-2">🤔</span>
                TRUTH
              </motion.button>
              <motion.button
                onClick={() => handleChoose('dare')}
                className="py-8 px-6 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl text-white font-bold text-xl shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-4xl block mb-2">💪</span>
                DARE
              </motion.button>
            </motion.div>
          )}

          {phase === 'prompt' && currentPrompt && (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="bg-black/30 rounded-2xl p-6 mb-6 text-center">
                <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold mb-4 ${
                  currentPrompt.type === 'truth' ? 'bg-blue-500' : 'bg-red-500'
                }`}>
                  {currentPrompt.type.toUpperCase()}
                </span>
                <p className="text-xl text-white font-medium">{currentPrompt.prompt}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  onClick={() => handleComplete(true)}
                  className="py-4 px-6 bg-gradient-to-br from-green-500 to-green-700 rounded-xl text-white font-bold text-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✅ Done!
                </motion.button>
                <motion.button
                  onClick={() => handleComplete(false)}
                  className="py-4 px-6 bg-gradient-to-br from-gray-600 to-gray-800 rounded-xl text-white font-bold text-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ❌ Skip
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default TruthOrDare;
